((global) => {
  const designs = typeof module !== 'undefined' && module.exports ? require('./rml-ready-designs.js') : global.RollMyLogoReadyDesigns;
  const sizes = typeof module !== 'undefined' && module.exports ? require('./rml-sizes.js') : global.RollMyLogoSizes;
  const formats = sizes.readyFormats;
  const diameters = Object.keys(formats).map(Number);
  const phraseGroups = Object.fromEntries(diameters.map(size => [size, Object.keys(designs || {}).filter(phrase => designs[phrase].listed !== false && designs[phrase].sizeKey === size)]));
  const phrases = Object.values(phraseGroups).flat();
  const displayPrices = Object.fromEntries(diameters.map(size => [size, '$' + formats[size].price]));
  const printSizeLabel = format => `${format.label} · Repeat: ${(format.repeatLengthMm / 25.4).toFixed(2)}\u2033 \u00d7 ${(format.widthMm / 25.4).toFixed(2)}\u2033 (${Number(format.repeatLengthMm.toFixed(1))} \u00d7 ${format.widthMm} mm)`;
  const inks = { '#09090a': 'Black', '#4157ff': 'Blue', '#e74132': 'Red', '#167548': 'Green', '#ffffff': 'White', '#f2c600': 'Yellow' };
  const defaultInk = '#09090a';
  const previewSurface = '#c9a36a';
  const isAllowed = (phrase, diameter) => phraseGroups[diameter]?.includes(phrase) === true;
  const cartKey = 'rollmylogo-demo-cart';
  const artwork = new Map();

  async function prepareDesigns(loadImage, createCanvas) {
    if (!designs || Object.keys(designs).length !== 14) throw new Error('Missing saved design library');
    await Promise.all(Object.entries(designs).map(async ([phrase, design]) => {
      const image = await loadImage(design.mask);
      if (!image.width || !image.height) throw new Error('Empty saved artwork');
      artwork.set(phrase, { image, createCanvas });
    }));
  }

  function renderImprint(canvas, phrase, diameterMm, ink = defaultInk, inverse = false, surfaceStyle) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!isAllowed(phrase, diameterMm)) return;
    const design = designs[phrase], format = formats[diameterMm];
    const lengthMm = format.repeatLengthMm;
    const bleedMm = (design.previewBleedPx || 0) / design.width * lengthMm;
    const scale = Math.min((canvas.width - 40) / (lengthMm + 2 * bleedMm), (canvas.height - 24) / format.widthMm);
    const width = lengthMm * scale;
    const height = format.widthMm * scale;
    const x = (canvas.width - width) / 2;
    const y = (canvas.height - height) / 2;
    const bleed = bleedMm * scale;
    const displayX = x - bleed, displayWidth = width + 2 * bleed;
    const color = Object.hasOwn(inks, ink) ? ink : defaultInk;
    const paper = surfaceStyle ? surfaceStyle(ctx, displayX, y, displayWidth, height) : previewSurface;
    ctx.fillStyle = inverse ? color : paper;
    ctx.fillRect(displayX, y, displayWidth, height);
    if (designs?.[phrase]) {
      const saved = artwork.get(phrase);
      if (!saved) { ctx.clearRect(0, 0, canvas.width, canvas.height); return; }
      const artworkWidth = width, artworkHeight = height;
      const left = x, top = y;
      const previewOffset = (design.previewShiftPx || 0) / design.width * width;
      const drawRepeat = image => {
        for (const turn of [-1, 0, 1]) ctx.drawImage(image, left + previewOffset + turn * width, top, artworkWidth, artworkHeight);
      };
      ctx.save();
      ctx.beginPath(); ctx.rect(displayX, y, displayWidth, height); ctx.clip();
      if (inverse) {
        ctx.globalCompositeOperation = 'destination-out';
        drawRepeat(saved.image);
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = paper;
        ctx.fillRect(displayX, y, displayWidth, height);
      } else {
        const tint = saved.createCanvas(saved.image.width, saved.image.height);
        const tinted = tint.getContext('2d');
        tinted.drawImage(saved.image, 0, 0);
        tinted.globalCompositeOperation = 'source-in';
        tinted.fillStyle = color;
        tinted.fillRect(0, 0, tint.width, tint.height);
        drawRepeat(tint);
      }
      ctx.restore();
      return { x, y, width, height, scale, artworkWidth, artworkHeight, previewOffset, bleed, displayX, displayWidth };
    }
  }

  function init(doc, win) {
    doc.querySelectorAll('[data-rml-ready]').forEach((root) => {
      const diameterInputs = Array.from(root.querySelectorAll('[data-ready-diameter]'));
      const catalog = Array.from(root.querySelectorAll('[data-ready-product-variant]'));
      const cards = Array.from(root.querySelectorAll('[data-ready-form]')).map((form) => ({
        form, phrase: form.dataset.readyPhrase, demo: form.hasAttribute('data-ready-demo'),
        canvas: form.querySelector('[data-ready-canvas]'),
        quantity: form.querySelector('[data-ready-quantity]'),
        add: form.querySelector('[data-ready-add]'),
        price: form.querySelector('[data-ready-price]'),
        size: form.querySelector('[data-ready-size]'),
        sizeField: form.querySelector('[data-ready-size-value]'),
        diameterField: form.querySelector('[data-ready-diameter-value]'),
        variant: form.querySelector('[data-ready-variant]'),
        variants: catalog.filter((node) => node.dataset.phrase === form.dataset.readyPhrase),
        design: form.querySelector('[data-ready-design]'),
        fontField: form.querySelector('[data-ready-font-value]'),
        widthField: form.querySelector('[data-ready-width-value]'),
        linesField: form.querySelector('[data-ready-lines-value]'),
        ink: defaultInk,
        inverse: false,
        inverseInput: form.querySelector('[data-ready-inverse]'),
        inverseField: form.querySelector('[data-ready-inverse-value]'),
        inkButtons: Array.from(form.querySelectorAll('[data-ready-ink]')),
        inkField: form.querySelector('[data-ready-ink-value]'),
        inkName: form.querySelector('[data-ready-ink-name]'),
        status: form.querySelector('[data-ready-card-status]'),
      }));
      const status = root.querySelector('[data-ready-status]');
      let fontReady = false, submitting = false;
      const diameter = () => diameterInputs.find((input) => input.checked);
      const variantFor = (card, size) => {
        const matches = card.variants.filter((node) => node.dataset.diameter === size?.value);
        return matches.length === 1 && /^\d+$/.test(matches[0].dataset.variant) ? matches[0].dataset : null;
      };
      const stockLimit = (variant) => /^\d+$/.test(variant?.stockLimit || '') ? Math.min(99, Number(variant.stockLimit)) : 99;
      const available = (card, size) => card.demo || (variantFor(card, size)?.available === 'true' && stockLimit(variantFor(card, size)) > 0);
      const designData = (card, size) => ({
        kind: 'ready-made', text: card.phrase, size: formats[size.value].label, clicheSize: formats[size.value].clicheSize, roller: formats[size.value].diameterMm,
        diameterLabel: size.value, repeatLengthMm: formats[size.value].repeatLengthMm, widthMm: formats[size.value].widthMm,
        fontKey: designs?.[card.phrase]?.key || 'oswald',
        fontFamily: designs?.[card.phrase]?.fontFamily || 'Oswald',
        fontWeight: designs?.[card.phrase]?.fontWeight || 700,
        fontStyle: designs?.[card.phrase]?.fontStyle || 'normal',
        charSpacing: designs?.[card.phrase]?.charSpacing || 0,
        artworkKey: designs?.[card.phrase]?.key,
        artworkRevision: designs?.[card.phrase]?.revision,
        previewPhaseMm: (designs[card.phrase].previewShiftPx || 0) / designs[card.phrase].width * formats[size.value].repeatLengthMm,
        ink: card.ink, surface: previewSurface, inverse: card.inverse, mode: 'saved-artwork',
        bridge: designs[card.phrase].bridge,
        rails: { continuous: designs[card.phrase].bridge === 'rings', top: designs[card.phrase].bridge === 'rings', bottom: designs[card.phrase].bridge === 'rings' },
      });
      const message = (node, text, error = false) => {
        node.textContent = text; node.dataset.error = String(error);
      };
      const paint = () => {
        const size = diameter();
        cards.forEach(({canvas, phrase, ink, inverse}) => {
          const rect = canvas.getBoundingClientRect?.();
          if (rect?.width && rect?.height) {
            const ratio = Math.min(win.devicePixelRatio || 1, 2);
            canvas.width = Math.round(rect.width * ratio);
            canvas.height = Math.round(rect.height * ratio);
          }
          const surfaceStyle = win.RollMyLogoKraft ? (...args) => win.RollMyLogoKraft.style(...args, Math.min(win.devicePixelRatio || 1, 2)) : undefined;
          renderImprint(canvas, phrase, Number(size?.value), ink, inverse, surfaceStyle);
        });
      };
      const update = () => {
        const size = diameter();
        root.dataset.readyFormat = size?.value || '';
        cards.forEach((card) => {
          const allowed = isAllowed(card.phrase, Number(size?.value));
          const inStock = available(card, size);
          const variant = variantFor(card, size);
          const price = !card.demo && variant?.price ? variant.price : displayPrices[size?.value] || '';
          card.form.hidden = !allowed;
          card.add.disabled = !fontReady || !allowed || !inStock || submitting;
          card.add.textContent = price ? `Add to cart - ${price}` : 'Add to cart';
          card.add.setAttribute('aria-label', `Add ${card.phrase} to cart${price ? ` - ${price}` : ''}`);
          card.size.textContent = size ? printSizeLabel(formats[size.value]) : '';
          if (card.sizeField) card.sizeField.value = formats[size.value].label;
          card.diameterField.value = size?.value || '';
          card.variant.value = card.demo ? '' : variant?.variant || '';
          card.quantity.max = String(card.demo ? 99 : Math.max(1, stockLimit(variant)));
          card.price.textContent = inStock ? `${price} each${!card.demo && variant?.stockLimit ? ` - ${stockLimit(variant)} in stock` : ''}` : variant ? 'Sold out' : 'Unavailable';
          card.inkField.value = inks[card.ink];
          card.inkName.textContent = inks[card.ink];
          if (card.inverseInput) { card.inverseInput.checked = card.inverse; card.inverseInput.disabled = submitting; }
          if (card.inverseField) card.inverseField.value = card.inverse ? 'Inverse' : 'Standard';
          card.design.value = allowed ? JSON.stringify(designData(card, size)) : '';
          if (card.widthField) card.widthField.value = String(formats[size.value].widthMm);
          if (card.linesField) card.linesField.value = designs[card.phrase].bridge === 'rings' ? 'Saved top and bottom lines' : 'Saved layout';
          if (card.fontField) {
            const design = designData(card, size);
            card.fontField.value = `${design.fontFamily} ${design.fontWeight}${design.fontStyle === 'italic' ? ' Italic' : ''}`;
          }
          card.inkButtons.forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.readyInk === card.ink));
            button.disabled = submitting;
          });
        });
        if (fontReady) paint();
      };
      diameterInputs.forEach((input) => input.addEventListener('change', () => {
        cards.forEach((card) => message(card.status, '')); update();
      }));
      cards.forEach((card) => card.inkButtons.forEach((button) => button.addEventListener('click', () => {
        if (submitting || !Object.hasOwn(inks, button.dataset.readyInk)) return;
        card.ink = button.dataset.readyInk;
        update();
      })));
      cards.forEach((card) => card.inverseInput?.addEventListener('change', () => {
        if (!submitting) card.inverse = card.inverseInput.checked;
        update();
      }));
      cards.forEach((card) => card.form.addEventListener('submit', (event) => {
        if (card.demo) event.preventDefault();
        const size = diameter(), count = Number(card.quantity.value);
        if (!fontReady || submitting || !isAllowed(card.phrase, Number(size?.value))) {
          event.preventDefault(); return;
        }
        if (!Number.isInteger(count) || count < 1 || count > 99) {
          event.preventDefault(); message(card.status, 'Choose a quantity between 1 and 99.', true);
          card.quantity.focus(); return;
        }
        if (!available(card, size)) {
          event.preventDefault(); message(card.status, 'This size is not available to order yet.', true); return;
        }
        const variant = variantFor(card, size);
        if (!card.demo && count > stockLimit(variant)) {
          event.preventDefault(); message(card.status, `Only ${stockLimit(variant)} available in this size.`, true);
          card.quantity.focus(); return;
        }
        const data = designData(card, size);
        card.design.value = JSON.stringify(data);
        card.variant.value = card.demo ? '' : variant.variant;
        card.diameterField.value = size.value;
        if (!card.demo) { submitting = true; update(); return; }
        try {
          const cart = JSON.parse(win.localStorage.getItem(cartKey) || '[]');
          if (!Array.isArray(cart)) throw new Error('Invalid cart');
          cart.push({ ...data, id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            title: `Ready-made roller: ${card.phrase}`, quantity: count, artwork: '', previewImage: card.canvas.toDataURL('image/png') });
          win.localStorage.setItem(cartKey, JSON.stringify(cart));
          submitting = true; update();
          doc.dispatchEvent(new win.CustomEvent('rollmylogo:cart-updated'));
          win.location.assign('cart.html');
        } catch (_error) {
          submitting = false; update();
          message(card.status, 'Could not save this order in your browser. Please check browser storage and try again.', true);
        }
      }));
      win.addEventListener('pageshow', () => { submitting = false; update(); });
      if (win.ResizeObserver) new win.ResizeObserver(() => { if (fontReady) paint(); }).observe(root);
      update(); message(status, 'Preparing designs...');
      win.RollMyLogoKraft?.ready.then(() => { if (fontReady) paint(); });
      const masks = win.Image ? prepareDesigns(src => new Promise((resolve, reject) => {
        const image = new win.Image(); image.onload = () => resolve(image); image.onerror = reject; image.src = src;
      }), (width, height) => {
        const canvas = doc.createElement('canvas'); canvas.width = width; canvas.height = height; return canvas;
      }) : artwork.size === 14 ? Promise.resolve() : Promise.reject(new Error('Artwork unavailable'));
      masks.then(() => {
        fontReady = true; message(status, ''); update();
      }).catch(() => message(status, 'Design previews could not be loaded. Please reload the page.', true));
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { phrases, phraseGroups, diameters, formats, inks, designs, prepareDesigns, renderImprint, init };
  else { global.RollMyLogoReady = { formats, diameters }; init(global.document, global); }
})(typeof window === 'undefined' ? globalThis : window);
